import { Container } from '@cleartrip/ct-design-components'
import React, { RefObject } from 'react'

import { getIsDynamicContentLoading, getWowFares, getWowLoadingFareIds } from '@/redux/slices/srpSlice'
import { useAppSelector } from '@/redux/store/hooks'

import BenifitSummary from '@/components/home/CFW/BenifitSummary'
import FareCardBadge from '@/components/srp/FareCards/FareBlock/Badge'
import FareBenefits from '@/components/srp/FareCards/FareBlock/FareBenefits'
import FareCardBottomCallout from '@/components/srp/FareCards/FareBlock/FareCardBottomCallout'
import FareDetails from '@/components/srp/FareCards/FareBlock/FareDetails'
import { getFareCabin } from '@/components/srp/FareCards/FareFamilyModal/crossCabinUtils'
import SelectedFareCardPattern from '@/components/srp/FareCards/FareFamilyModal/SelectedFareCardPattern'
import WowFareCallout from '@/components/srp/Tupple/WowFareCallout'
import { CabinTabKey, FIRST_FARE_CARD_PATTERN, getCabinInterstitialTheme, isFirstClassCabinTab, isSrpThemeCabin, PE_FARE_CARD_PATTERN } from '@/constants/flights/premiumEconomy'
import { CLEARTIP_EXCLUSIVE, STUDENT_FARE } from '@/constants/Srp'
import useWowPricingExperiment from '@/hooks/srp/useWowPricingExperiment'
import { FareType, ServicesData } from '@/types/srp'
import { isEmpty } from '@/utils/general/browserHelper'
import { isUserSignedIn } from '@/utils/general/user'
import { getFFBottomCallout, isCTBrandedFareCardCheck, isCTBrandedFareCardSelectedOnward } from '@/utils/srp'
import { CT_UPGRADE_BENEFIT_TYPE } from '@/utils/flights/smbVasCoupon'
import { getPtcTagData, getSlasherCouponCode } from '@/utils/srp/helper'
import { isFareVisaRejectionEligibleForDisplay, isVisaFreeForFare } from '@/utils/srp/visaRejection'

interface FareCardProps {
  fare: FareType
  benefitRef: RefObject<HTMLDivElement>
  additionalBenefitsWorth?: number
  showPerAdultPricing: boolean
  isSelected: boolean
  selectFare: () => void
  isPremiumCard: boolean
  showBadge: boolean
  servicesData: ServicesData
  isServicesLoading: boolean
  retryCountForServices: number
  onRetryForBenefits: () => void
  couponCallout: string
  displayPrice: number
  strikeOffPrice: number
  displaySubTitle: string
  displayTitle: string
  fareId: string
  travelOptionId: string
  isSubtitlePresent: boolean
  showLeftTooltip?: boolean
  isFFFlow?: boolean
  ptcTag?: string
  isCouponPresentInAnyFare?: boolean
  isSpecialRT?: boolean
  isSecond?: boolean
  isCfw?: boolean
  isIntl?: boolean
  /** True when rendered inside an RT FareFamilyModal — switches the
   *  per-fare WOW lookup to use the combined `[onwardFareId, returnFareId]`
   *  key. The opposite leg's currently-selected fareId is supplied via
   *  `otherTabSelectedFareId`. */
  isRT?: boolean
  /** When `isRT`, the currently-selected fareId on the OPPOSITE tab. */
  otherTabSelectedFareId?: string
  /** When `isRT`, which tab this card belongs to (0 = onward, 1 = return). */
  rtTabIndex?: number
  selectedTabIndex?: number
  selectedFaresObj?: any
  onDisabledFareClick?: () => void
  onOpenVdcEducation?: () => void
  cardDetails?: any[]
  subTravelOptions?: Record<string, any>
  isPeUpgradeAttached?: boolean
  selectedCabin?: CabinTabKey
  isCrossCabinGuardEnabled?: boolean
  /**
   * True when the parent already filtered this strip to `selectedCabin` via BE
   * `cabinFareIds` (or special-RT cabin filter). In that case tab membership is
   * authoritative — `resolveDisplayCabin` can disagree (missing `cabinType`
   * defaults to ECONOMY) and must not grey out Select / Selected.
   */
  isCabinFilteredList?: boolean
  /**
   * RT return tab: cabin tab selected on the onward leg. Preferred over
   * resolveDisplayCabin(onwardFare) when the strip is cabin-filtered.
   */
  onwardLegCabin?: CabinTabKey
  /** `showPeInterstitialLayout` — pe_discovery_v1 cookie/Statsig gate is already resolved upstream. */
  isPeDiscoveryActive?: boolean
  /**
   * When any sibling in the strip has a bottom benefits strip (Value Max
   * BenifitSummary or "Additional benefits worth"), stretch this card and
   * reserve empty space at the bottom if this fare has no strip of its own.
   */
  equalizeBottomBenefitsSpace?: boolean
  /**
   * Measured tallest card-shell height in the strip (px). Applied as minHeight so
   * shorter cards grow with a white footer instead of relying on flex stretch
   * (which was leaving borders cut off mid-card).
   */
  equalizeMinHeightPx?: number
  smbVasCouponCallout?: string
  /** SplRT: from couponUIResponse…nepDetails.slasherCouponCode; else fare pricing is used. */
  slasherCouponCode?: string
}
const FareCard = ({
  fare,
  benefitRef,
  additionalBenefitsWorth: _additionalBenefitsWorth,
  showPerAdultPricing,
  isSelected,
  selectFare,
  showBadge,
  isPremiumCard,
  servicesData,
  isServicesLoading,
  retryCountForServices,
  onRetryForBenefits,
  couponCallout,
  displayPrice,
  strikeOffPrice,
  displaySubTitle,
  displayTitle,
  fareId,
  travelOptionId,
  isSubtitlePresent,
  showLeftTooltip = false,
  isFFFlow,
  ptcTag = '',
  isCouponPresentInAnyFare,
  isSpecialRT = false,
  isSecond: _isSecond,
  isCfw = false,
  isIntl = false,
  isRT = false,
  otherTabSelectedFareId,
  rtTabIndex,
  selectedTabIndex = 0,
  selectedFaresObj = [],
  onDisabledFareClick,
  onOpenVdcEducation,
  cardDetails = [],
  subTravelOptions = {},
  isPeUpgradeAttached = false,
  selectedCabin,
  isCrossCabinGuardEnabled = false,
  isCabinFilteredList = false,
  onwardLegCabin,
  isPeDiscoveryActive = false,
  equalizeBottomBenefitsSpace = false,
  equalizeMinHeightPx,
  smbVasCouponCallout = '',
  slasherCouponCode: nepSlasherCouponCode = '',
}: FareCardProps) => {
  const isCTBrandedFareCard = isCTBrandedFareCardCheck(fare)
  const { farePerks, displayText } = fare || {}
  const { perks, logo, displayText: farePerksDisplayText = '' } = farePerks || {}
  const isCTBrandedFareCardSelectedOW = isCTBrandedFareCardSelectedOnward(selectedFaresObj)
  const useGradientBackground = isCTBrandedFareCard
  const ffBottomCallout = getFFBottomCallout(fare)
  const hasFFBottomCallout = !isCTBrandedFareCard && !!isFFFlow && ffBottomCallout !== null
  const isCtUpgradeCallout = ffBottomCallout?.benefitType === CT_UPGRADE_BENEFIT_TYPE
  const onwardSelectedCabin =
    !!selectedTabIndex && !isSpecialRT && !isEmpty(selectedFaresObj?.[0])
      ? (isCabinFilteredList && onwardLegCabin
          ? onwardLegCabin
          : getFareCabin(selectedFaresObj[0]))
      : undefined
  const fareCabin = getFareCabin(fare)
  // When the strip is already cabin-filtered, trust the active tab — BE membership
  // can place fares whose `cabinType` is missing (resolveDisplayCabin → ECONOMY) on
  // a PE/Business/First-only strip (tabs hidden). Using resolveDisplayCabin there
  // greys out Select/Selected. On the unfiltered legacy list, still enforce
  // selectedCabin vs fareCabin so a stale card can't be picked off-tab (CTAIR-2552).
  const fareCabinForGuard =
    isCabinFilteredList && selectedCabin ? selectedCabin : fareCabin
  const isCrossCabinDisabled =
    isCrossCabinGuardEnabled &&
    !!fareCabinForGuard &&
    ((!!onwardSelectedCabin && onwardSelectedCabin !== fareCabinForGuard) ||
      (!isCabinFilteredList && !!selectedCabin && selectedCabin !== fareCabin))
  const isDisabledFareCard =
    isCrossCabinDisabled ||
    (!!selectedTabIndex &&
      ((isCTBrandedFareCardSelectedOW && !isCTBrandedFareCard) || (!isCTBrandedFareCardSelectedOW && isCTBrandedFareCard)))
  const isVisaAvailable = isFareVisaRejectionEligibleForDisplay(fare, {
    isSpecialRT,
    cardDetails,
    subTravelOptions,
  })
  const isVisaFree = isVisaAvailable && isVisaFreeForFare(fare)
  const ptcTagData = getPtcTagData(fare) || {}
  const showPtcTag = isIntl && ptcTagData.benefitType === STUDENT_FARE
  const greenBadgeCondition = showBadge || !isEmpty(ptcTag)
  const showGreenTopBadge = greenBadgeCondition && !showPtcTag
  const cabinTheme = getCabinInterstitialTheme(selectedCabin ?? 'ECONOMY')
  const fareCardBorderGradient = cabinTheme.fareCardBorderGradient
  const showGradientBorder = !!fareCardBorderGradient && !isSelected
  const cabinFareCardBorderColor = cabinTheme.fareCardBorderColor
  const useCabinBorderColor = !showGradientBorder && !isSelected && !!cabinFareCardBorderColor
  const selectedCabinCardBackground =
    isSelected && cabinTheme.selectedFareCardBackground ? cabinTheme.selectedFareCardBackground : undefined
  // Cards on a Premium Economy / Business / First Class tab of the PE-discovery
  // interstitial. Economy tabs are untouched.
  const isPeThemedCard = isPeDiscoveryActive && isSrpThemeCabin(selectedCabin)
  const ctUpgradeText =
    isCtUpgradeCallout && !isPeThemedCard ? ffBottomCallout?.benefitText || '' : ''
  // Premium and CT-branded cards paint a purple wash across three separate layers.
  // Themed cabins carry their own gold treatment, so the whole strip stays plain
  // white inside the gold border (Figma 4143:78021) — suppress all three together.
  const useBrandedPurple = useGradientBackground && !isPeThemedCard
  const premiumCardBackground =
    isPremiumCard && !isPeThemedCard ? 'linear-gradient(171deg, #F0E9FF 0%, #FFF 42.35%)' : '#ffffff'
  // Only the Value Max card grows the cream benefits footer, and only that card
  // hands its bottom rounding over to it. Same when a white equalize spacer
  // sits under the content — square the content bottom so gold can't peek
  // through rounded corner notches.
  const showThemedBenefitsFooter = isPeThemedCard && isCTBrandedFareCard && !isEmpty(farePerks)
  const showBottomCallout = hasFFBottomCallout && !isPeThemedCard && !isCtUpgradeCallout
  const hasBottomBenefitsStrip = showBottomCallout || (isCTBrandedFareCard && !isEmpty(farePerks))
  const hasWhiteEqualizeSpacer = equalizeBottomBenefitsSpace && !hasBottomBenefitsStrip
  const squareContentBottom = showThemedBenefitsFooter || hasWhiteEqualizeSpacer
  // Only equalize when the strip opted in — keeps Economy / no-callout strips identical.
  const shouldEqualizeHeight = equalizeBottomBenefitsSpace && (equalizeMinHeightPx ?? 0) > 0
  // Decorative motif on the selected themed fare card — bottom-right arcs on the
  // gold cabins (Figma 512:11443), top-right lattice on First Class (1701:85133).
  const showSelectedCardPattern = isSelected && isPeThemedCard
  const selectedCardPattern = isFirstClassCabinTab(selectedCabin ?? 'ECONOMY')
    ? FIRST_FARE_CARD_PATTERN
    : PE_FARE_CARD_PATTERN
  // WOW pricing — per-fare callout.
  //  - OW: lookup by individual fareId.
  //  - RT: lookup by COMBINED key built from this fare + opposite tab's
  //    currently-selected fare.
  const wowFares = useAppSelector(getWowFares)
  const isDynamicContentLoading = useAppSelector(getIsDynamicContentLoading)
  const wowLoadingFareIds = useAppSelector(getWowLoadingFareIds)
  const { wowOn } = useWowPricingExperiment(!!isIntl)
  let wowLookupKey: string | undefined = fareId
  if (isRT && fareId && otherTabSelectedFareId) {
    wowLookupKey =
      rtTabIndex === 1
        ? `[${otherTabSelectedFareId}, ${fareId}]`
        : `[${fareId}, ${otherTabSelectedFareId}]`
  }
  const canRenderWow = (!isRT || !!otherTabSelectedFareId) && isUserSignedIn()
  // Check whether this fare has actual WOW discount data (not just an entry in wowFares).
  // WowFareCallout returns null when wowApplicable=false and message='', so we must
  // fall back to FareCouponCallout in that case rather than hiding it.
  const wowFareData = wowFares?.[wowLookupKey as string]
  const wowPricingObject = wowFareData
    ? (showPerAdultPricing ? wowFareData?.pricing?.perAdultPricing : wowFareData?.pricing?.totalPricing)
    : undefined
  const { wowApplicable = false } = wowPricingObject || {}
  const wowMessage = wowPricingObject?.couponDetails?.message || ''
  const hasActualWowContent = !!(wowApplicable || wowMessage)
  const isWowLoading = isDynamicContentLoading || !!(wowLookupKey && wowLoadingFareIds?.includes(wowLookupKey))
  const hasWowContent = isWowLoading || hasActualWowContent
  const wowListOfFares = wowLookupKey
    ? [{ fareId: wowLookupKey, showPerAdultPricing: !!showPerAdultPricing, couponCallout: couponCallout ?? '' }]
    : []
  // SplRT: only nepDetails.slasherCouponCode. Else fare pricing.couponDetails.slasherCouponCode.
  const slasherCouponCode = isSpecialRT
      ? nepSlasherCouponCode || ''
      : getSlasherCouponCode({ fare, showPerAdultPricing })
  const wowCallout =
    wowOn && wowLookupKey && canRenderWow ? (
        <WowFareCallout
          isDynamicContentLoading={isDynamicContentLoading}
          isIntl={!!isIntl}
          listOfFares={wowListOfFares}
          wowFares={wowFares}
        />
    ) : null
  return (
    // Non-clipping positioning wrapper: hosts the absolutely-positioned top
    // badges so their top edge (top: -11px) is never clipped by the card box's
    // `o-hidden` (which the gradient-border premium theme relies on).
    <Container
      className='p-relative ml-4 flex flex-column'
      style={{
        width: '236px',
        // fixes layout shift on fare select
        marginTop: isPeUpgradeAttached || isSelected ? '0px' : '1px',
        marginBottom: isPeUpgradeAttached || isSelected ? '0px' : '1px',
        marginRight: isPeUpgradeAttached ? '0px' : isSelected ? '0px' : '1px',
        ...(isPeUpgradeAttached ? { zIndex: 1 } : {}),
        // Keep top badges above the opaque card shell (CLEARTRIP EXCLUSIVE, etc.).
        isolation: 'isolate',
        // Do not stretch in the scroll track — flex-grow + equalize height
        // feedback made shells ratchet taller on every select.
      }}
    >
      <Container
        className={`br-16 p-relative flex flex-column ${
          showGradientBorder
            ? ''
            : `ba-solid ${isSelected ? 'bc-neutral-900' : useCabinBorderColor ? '' : 'bc-line-500'}`
        } ${useBrandedPurple ? 'bg-blue-100' : ''}`}
        data-fare-equalize-root=''
        style={{
          width: '236px',
          boxSizing: 'border-box',
          // Equalize only when the strip measured a tallest sibling — otherwise
          // height stays content-driven (original Economy / no-callout behaviour).
          // Explicit height only (no flex-grow) so a stretched scroll-track
          // parent cannot pull the shell taller than the measured max.
          ...(shouldEqualizeHeight
            ? {
                height: `${equalizeMinHeightPx}px`,
                minHeight: `${equalizeMinHeightPx}px`,
                flex: '0 0 auto',
                // Clip the gold/solid border shell around the full card so the
                // bottom edge closes; fee tooltips portal to document.body.
                overflow: 'hidden',
              }
            : {
                // Clip selected-card pattern to the rounded shell; otherwise keep
                // overflow visible so fee tooltips aren't clipped.
                overflow: showSelectedCardPattern || showGradientBorder ? 'hidden' : undefined,
              }),
          ...(showGradientBorder
            ? { padding: '1px', background: fareCardBorderGradient }
            : {
                borderWidth: isSelected ? '2px' : '1px',
                ...(useCabinBorderColor ? { borderColor: cabinFareCardBorderColor } : {}),
                ...(selectedCabinCardBackground ? { background: selectedCabinCardBackground } : {}),
              }),
          ...(isPeUpgradeAttached
            ? { backgroundColor: useBrandedPurple ? undefined : '#ffffff' }
            : {}),
        }}
      >
        {/* Fill column — opaque white on short cards so the gold 1px gradient
            border never shows through empty footer space / corner notches. */}
        <Container
          className={
            shouldEqualizeHeight || showThemedBenefitsFooter || showSelectedCardPattern
              ? 'flex flex-column flex-1 p-relative'
              : undefined
          }
          css={
            shouldEqualizeHeight || showSelectedCardPattern
              ? {
                  flex: shouldEqualizeHeight ? '1 1 auto' : undefined,
                  alignSelf: shouldEqualizeHeight ? 'stretch' : undefined,
                  display: 'flex',
                  flexDirection: 'column',
                  ...(shouldEqualizeHeight
                    ? {
                        height: '100%',
                        minHeight: '100%',
                      }
                    : {}),
                  // Short unselected cards: solid white covers the gold shell.
                  // Selected short cards / Value Max: keep transparent so gold
                  // wash / cream BenifitSummary still show through.
                  background:
                    shouldEqualizeHeight && hasWhiteEqualizeSpacer && !selectedCabinCardBackground
                      ? '#ffffff'
                      : 'transparent',
                  borderRadius: showGradientBorder
                    ? '15px'
                    : isSelected
                      ? '14px'
                      : '16px',
                  overflow: 'hidden',
                  position: 'relative',
                }
              : undefined
          }
        >
          {showSelectedCardPattern ? (
            <SelectedFareCardPattern pattern={selectedCardPattern} />
          ) : null}
          {/* Content + footers above the PE corner pattern (z-index). */}
          <Container
            css={{
              position: 'relative',
              zIndex: 1,
              display: 'flex',
              flexDirection: 'column',
              flex: shouldEqualizeHeight ? '1 1 auto' : undefined,
              minHeight: shouldEqualizeHeight ? 0 : undefined,
            }}
          >
            <Container
              className={squareContentBottom ? '' : 'br-16'}
              css={{
                background: showGradientBorder
                  ? premiumCardBackground
                  : selectedCabinCardBackground
                    ? 'transparent'
                    : premiumCardBackground,
                position: 'relative',
                flexShrink: 0,
                ...(squareContentBottom
                  ? {
                      borderRadius: showGradientBorder
                        ? '15px 15px 0 0'
                        : isSelected
                          ? '14px 14px 0 0'
                          : '16px 16px 0 0',
                    }
                  : {}),
              }}
            >
              <Container
                className={`px-4 pt-4 br-16 flex flex-column br-16 ${useBrandedPurple ? 'bg-gradient-2J' : ''}`}
              >
                <FareDetails
                  calloutNode={hasWowContent ? wowCallout : undefined}
                  couponCallout={couponCallout}
                  ctUpgradeText={ctUpgradeText}
                  displayPrice={displayPrice}
                  displaySubTitle={displaySubTitle}
                  displayTitle={displayTitle}
                  isCouponPresentInAnyFare={isCouponPresentInAnyFare}
                  isCTBrandedFareCard={isCTBrandedFareCard}
                  isDisabledFareCard={isDisabledFareCard}
                  isFFFlow={isFFFlow}
                  isSelected={isSelected}
                  isSubtitlePresent={isSubtitlePresent}
                  selectFare={selectFare}
                  showPerAdultPricing={showPerAdultPricing}
                  showPtcTag={showPtcTag}
                  slasherCouponCode={slasherCouponCode}
                  smbVasCouponCallout={smbVasCouponCallout}
                  strikeOffPrice={strikeOffPrice}
                  titleLogo={displayText.titleLogo}
                  onDisabledFareClick={onDisabledFareClick}
                />
                <FareBenefits
                  fareId={fareId}
                  isIntl={isIntl}
                  isServicesLoading={isServicesLoading}
                  isSpecialRT={isSpecialRT}
                  isVisaAvailable={isVisaAvailable}
                  isVisaFree={isVisaFree}
                  retryCountForServices={retryCountForServices}
                  servicesData={servicesData}
                  showLeftTooltip={showLeftTooltip}
                  travelOptionId={travelOptionId}
                  onOpenVdcEducation={onOpenVdcEducation}
                  onRetryForBenefits={onRetryForBenefits}
                />
              </Container>
            </Container>
            {showBottomCallout && <FareCardBottomCallout fare={fare} />}
            {isCTBrandedFareCard && !isEmpty(farePerks) ? (
              <BenifitSummary
                benefitRef={benefitRef}
                benefits={perks}
                icon={logo}
                isThemedCabin={isPeThemedCard}
                title={farePerksDisplayText}
              />
            ) : null}
            {hasWhiteEqualizeSpacer ? (
              <Container
                aria-hidden
                css={{
                  flex: '1 1 auto',
                  minHeight: 0,
                  alignSelf: 'stretch',
                  // Selected PE cards keep the gold wash / corner pattern visible.
                  background: selectedCabinCardBackground ? 'transparent' : '#ffffff',
                }}
              />
            ) : null}
          </Container>
        </Container>
      </Container>

      {/* After the card shell so the pill paints above the opaque top border. */}
      {showGreenTopBadge ? <FareCardBadge badgeBackground={cabinTheme.badgeBackground} text={ptcTag || 'BEST VALUE'} /> : null}
      {isCTBrandedFareCard ? (
        <FareCardBadge badgeBackground="#5F1AB6" text={CLEARTIP_EXCLUSIVE} />
      ) : null}
    </Container>
  )
}

export default FareCard